<template>
  <v-card outlined class="historico-comunicacao">
    <v-toolbar flat dense color="primary" dark>
      <v-icon left>mdi-whatsapp</v-icon>
      <v-toolbar-title>Comunicação com o paciente</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn
        v-if="podeEnviarTeste"
        small
        outlined
        :loading="enviandoTeste"
        @click="enviarTeste"
      >
        <v-icon left small>mdi-send-check</v-icon>
        Enviar teste
      </v-btn>
    </v-toolbar>

    <v-card-text>
      <!-- Toggle de consentimento (LGPD art. 18, V) -->
      <v-row dense align="center">
        <v-col cols="12" md="8">
          <v-switch
            v-model="aceitaComunicacao"
            :loading="salvandoConsentimento"
            :disabled="salvandoConsentimento"
            inset
            dense
            label="Paciente aceita receber comunicações por WhatsApp"
            hint="Confirmações de agendamento, lembretes e pós-consulta. O paciente pode revogar a qualquer momento (LGPD)."
            persistent-hint
            @change="alterarConsentimento"
          ></v-switch>
        </v-col>
      </v-row>

      <v-divider class="my-3"></v-divider>

      <!-- Histórico de envios -->
      <v-data-table
        :headers="headers"
        :items="itens"
        :loading="carregando"
        :server-items-length="total"
        :options.sync="opcoes"
        :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
        dense
        no-data-text="Nenhuma comunicação enviada a este paciente."
        loading-text="Carregando histórico..."
      >
        <template v-slot:[`item.enviadoEm`]="{ item }">
          {{ formatarData(item.enviadoEm) }}
        </template>

        <template v-slot:[`item.tipo`]="{ item }">
          {{ descreverTipo(item.tipo) }}
        </template>

        <template v-slot:[`item.status`]="{ item }">
          <v-tooltip bottom :disabled="!item.erroDetalhes">
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                small
                :color="corStatus(item.status)"
                text-color="white"
                v-bind="attrs"
                v-on="on"
              >
                {{ descreverStatus(item.status) }}
              </v-chip>
            </template>
            <span>{{ item.erroDetalhes }}</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import Vue from 'vue';
import { ComunicacaoService } from '@/services/ComunicacaoService';
import { MensagemMixin } from '@/helpers/MensagemHelper';
import { decode, getToken } from '@/utils/Token';
import PermissoesPadrao from '@/constants/enums/PermissoesPadrao';

// Espelha TipoComunicacao/StatusComunicacao do backend (valores estáveis, não renumerar).
const TIPOS: Record<number, string> = {
  1: 'Confirmação de agendamento',
  2: 'Lembrete de medicação',
  3: 'Lembrete de exame pendente',
  4: 'Lembrete de retorno',
  5: 'Sumário pós-consulta',
  6: 'Pesquisa de satisfação (NPS)',
};

const STATUS: Record<number, string> = {
  1: 'Enviado',
  2: 'Falha',
  3: 'Suprimido',
};

export default Vue.extend({
  name: 'soi-historico-comunicacao',
  props: {
    pacienteId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      service: new ComunicacaoService(),
      itens: [] as any[],
      total: 0,
      aceitaComunicacao: true,
      carregando: false,
      salvandoConsentimento: false,
      enviandoTeste: false,
      opcoes: {
        page: 1,
        itemsPerPage: 10,
      } as any,
      headers: [
        { text: 'Data', value: 'enviadoEm', sortable: false },
        { text: 'Tipo', value: 'tipo', sortable: false },
        { text: 'Status', value: 'status', sortable: false },
        { text: 'Telefone', value: 'telefone', sortable: false },
        { text: 'Resumo', value: 'resumoConteudo', sortable: false },
      ],
    };
  },
  computed: {
    podeEnviarTeste(): boolean {
      try {
        const sessaoUsuario = decode(getToken());
        return sessaoUsuario.permissions.includes(PermissoesPadrao.WhatsAppEnviarMensagens);
      } catch (err) {
        return false;
      }
    },
  },
  watch: {
    opcoes: {
      handler() {
        this.carregarHistorico();
      },
      deep: true,
    },
    pacienteId() {
      this.opcoes.page = 1;
      this.carregarHistorico();
    },
  },
  methods: {
    async carregarHistorico() {
      if (!this.pacienteId) return;
      this.carregando = true;
      try {
        const res = await this.service.ObterHistorico(
          this.pacienteId,
          this.opcoes.page,
          this.opcoes.itemsPerPage,
        );
        this.itens = res.data.itens || [];
        this.total = res.data.total || 0;
        this.aceitaComunicacao = !!res.data.aceitaComunicacaoWhatsApp;
      } catch (err) {
        MensagemMixin(3000, 'error', 'Não foi possível carregar o histórico de comunicações.');
      } finally {
        this.carregando = false;
      }
    },

    async alterarConsentimento(valor: boolean) {
      this.salvandoConsentimento = true;
      try {
        await this.service.RegistrarOptOut(this.pacienteId, valor);
        MensagemMixin(
          3000,
          'success',
          valor
            ? 'Consentimento de comunicação registrado.'
            : 'Opt-out registrado: o paciente não receberá mais comunicações por WhatsApp.',
        );
      } catch (err) {
        // Reverte o toggle se a gravação falhou — a tela nunca pode divergir do banco.
        this.aceitaComunicacao = !valor;
        MensagemMixin(3000, 'error', 'Não foi possível atualizar o consentimento.');
      } finally {
        this.salvandoConsentimento = false;
      }
    },

    async enviarTeste() {
      this.enviandoTeste = true;
      try {
        const res = await this.service.EnviarTeste(this.pacienteId);
        const status = res.data && res.data.status;
        if (status === 1) {
          MensagemMixin(3000, 'success', 'Mensagem de teste enviada com sucesso.');
        } else if (status === 3) {
          MensagemMixin(4000, 'warning', 'Envio suprimido (opt-out, módulo desligado ou telefone inválido).');
        } else {
          MensagemMixin(4000, 'error', 'Falha no envio de teste. Verifique a instância WhatsApp da clínica.');
        }
        this.carregarHistorico();
      } catch (err: any) {
        const msg = (err && err.response && err.response.data) || 'Não foi possível enviar a mensagem de teste.';
        MensagemMixin(4000, 'error', msg);
      } finally {
        this.enviandoTeste = false;
      }
    },

    descreverTipo(tipo: number): string {
      return TIPOS[tipo] || `Tipo ${tipo}`;
    },

    descreverStatus(status: number): string {
      return STATUS[status] || `Status ${status}`;
    },

    corStatus(status: number): string {
      if (status === 1) return 'success';
      if (status === 2) return 'error';
      return 'grey';
    },

    formatarData(iso: string): string {
      if (!iso) return '';
      // EnviadoEm vem em UTC do backend — exibe no fuso local.
      const data = new Date(iso.endsWith('Z') ? iso : `${iso}Z`);
      return data.toLocaleString('pt-BR');
    },
  },
});
</script>

<style scoped>
.historico-comunicacao {
  width: 100%;
}
</style>
